import CheckCircle from "@mui/icons-material/CheckCircle";
import ChevronRight from "@mui/icons-material/ChevronRight";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { Box, Button, Stack, Typography } from "@mui/material";
import { FOLLOW_UP_LABEL } from "encamp-shared/src/waste/discrepancyDisplayStatus";

import {
  type BreakdownInput,
  buildDiscrepancyBreakdown,
  type StatusCounts,
} from "./discrepancyBreakdown";
import { SectionHeader } from "./SectionHeader";

interface DiscrepanciesSectionProps {
  /** The manifest's discrepancy rows (already flag-filtered upstream). */
  discrepancies: readonly BreakdownInput[];
  /** Open the review modal. */
  onOpenDiscrepancies: () => void;
  /**
   * Days until the earliest open EPA correction deadline (negative = overdue).
   * Null when the manifest has no open EPA corrections — the footer is hidden.
   */
  epaCorrectionDueInDays?: number | null;
  /** Reviewing is staff-only, so the "Review all" entry only renders for staff. */
  canReview: boolean;
  /**
   * Customer read-only mode (manifest-views-v2 tenants): shows a "View All"
   * action that opens the read-only discrepancy modal via `onOpenDiscrepancies`.
   * Pass rows already filtered to the customer-visible set (Field kind,
   * non-IGNORED).
   */
  customerView?: boolean;
  /**
   * Render every breakdown row (each kind), even those with a zero total, instead
   * of only the populated ones — and keep the table (no empty-state) when there
   * are no discrepancies. Used by the waste-stream drawer, where a stable
   * three-row layout reads better than a collapsing one.
   */
  alwaysShowAllRows?: boolean;
  /** Empty-state copy; defaults to the manifest-drawer wording. */
  emptyMessage?: string;
}

/** Human phrasing for the EPA-correction-deadline footer. */
function epaCorrectionDueMessage(days: number): string {
  if (days < 0) {
    const overdue = Math.abs(days);
    return `First EPA correction overdue by ${overdue} ${overdue === 1 ? "day" : "days"}`;
  }
  if (days === 0) return "First EPA correction due today";
  return `First EPA correction due in ${days} ${days === 1 ? "day" : "days"}`;
}

// On the manifest page color already encodes data source, so the status counts
// stay neutral (text.primary) rather than reusing warning/info/success — which
// would give color two meanings on the same screen.
type StatusColumn = {
  key: string;
  label: string;
  color: string;
  value: (counts: StatusCounts) => number;
};

// Staff and customers share the same status columns and the same counts.
// Dismissed (IGNORED) rows are non-discrepancies and never count
// (buildDiscrepancyBreakdown skips them), matching the grid circles'
// fieldResolved semantics.
const STATUS_COLUMNS: StatusColumn[] = [
  { key: "unresolved", label: "Pending Review", color: "text.primary", value: (c) => c.unresolved },
  {
    key: "pending",
    label: "Needs correction in e-Manifest",
    color: "text.primary",
    value: (c) => c.pending,
  },
  { key: "resolved", label: "Resolved", color: "text.primary", value: (c) => c.resolved },
];

// Only a missing e-Manifest row can be in follow-up, so the column would be
// empty on most manifests. It appears, ahead of Resolved, only when a row is in it.
const FOLLOW_UP_COLUMN: StatusColumn = {
  key: "followUp",
  label: FOLLOW_UP_LABEL,
  color: "text.primary",
  value: (c) => c.followUp,
};

function statusColumnsFor(counts: StatusCounts): StatusColumn[] {
  if (counts.followUp === 0) return STATUS_COLUMNS;
  const resolvedAt = STATUS_COLUMNS.findIndex((col) => col.key === "resolved");
  return [
    ...STATUS_COLUMNS.slice(0, resolvedAt),
    FOLLOW_UP_COLUMN,
    ...STATUS_COLUMNS.slice(resolvedAt),
  ];
}

// label | status columns | total. Status columns are wide enough for "Pending
// Review" when there is room for them.
//
// Every track is shrinkable, because the fixed widths did not fit the card at
// every layout. This section and Compliance sit side by side down to the `md`
// breakpoint, so around 1100px each card is roughly 400px wide while the fixed
// tracks wanted 104×3 + 64 plus gaps. A `1fr` label collapses to zero before a
// fixed track gives up anything, so the surplus fell off the right edge and
// clipped the Total column instead.
//
// The label keeps a floor rather than a share: at its widest it takes the
// leftover space, and at its narrowest it still fits a wrapped "Data Source
// Conflicts". The count columns cap at the width that fits their headers and
// shrink from there, so the numbers stay inside the card and the headers wrap.
const gridColumns = (statusColumnCount: number) =>
  `minmax(88px, 1fr) repeat(${statusColumnCount}, minmax(0, 104px)) minmax(0, 64px)`;

/** A single count cell: colored when non-zero, a muted dash at zero. */
function CountCell({ value, color, bold }: { value: number; color: string; bold?: boolean }) {
  if (value === 0) {
    return (
      <Typography variant="body2" align="right" sx={{ color: "text.disabled" }}>
        –
      </Typography>
    );
  }
  return (
    <Typography variant="body2" align="right" sx={{ color, fontWeight: bold ? 700 : 600 }}>
      {value}
    </Typography>
  );
}

/** The status + total cells shared by the column header and each row. */
function CountCells({ counts, columns }: { counts: StatusCounts; columns: StatusColumn[] }) {
  return (
    <>
      {columns.map((col) => (
        <CountCell key={col.key} value={col.value(counts)} color={col.color} />
      ))}
      <CountCell value={counts.total} color="text.primary" bold />
    </>
  );
}

function HeaderAction({
  canReview,
  customerView,
  onOpenDiscrepancies,
}: {
  canReview: boolean;
  customerView: boolean;
  onOpenDiscrepancies: () => void;
}) {
  if (canReview) {
    return (
      <Button
        size="small"
        color="staff"
        endIcon={<ChevronRight />}
        onClick={() => onOpenDiscrepancies()}
        sx={{ flexShrink: 0 }}
      >
        Review all
      </Button>
    );
  }
  if (!customerView) return null;
  return (
    <Button
      size="small"
      endIcon={<ChevronRight />}
      onClick={() => onOpenDiscrepancies()}
      sx={{ flexShrink: 0 }}
    >
      View All
    </Button>
  );
}

const rowSx = (statusColumnCount: number) =>
  ({
    display: "grid",
    gridTemplateColumns: gridColumns(statusColumnCount),
    alignItems: "center",
    columnGap: 1,
    px: 1,
    py: 0.5,
    borderRadius: 1,
  }) as const;

/**
 * Discrepancies at a glance: a compact matrix of top-line rows (EPA correction
 * requests, missing e-Manifest, Encamp-flagged discrepancies) against status
 * (Pending Review / Needs correction in e-Manifest / Resolved) with totals. The rows are read-only;
 * staff open the review modal via the "Review all" action. Source-agnostic.
 */
export function DiscrepanciesSection({
  discrepancies,
  onOpenDiscrepancies,
  epaCorrectionDueInDays,
  canReview,
  customerView = false,
  alwaysShowAllRows = false,
  emptyMessage = "No discrepancies on this manifest",
}: DiscrepanciesSectionProps) {
  const breakdown = buildDiscrepancyBreakdown(discrepancies);
  const rowsToShow = alwaysShowAllRows
    ? breakdown.rows
    : breakdown.rows.filter((r) => r.counts.total > 0);
  const statusColumns = statusColumnsFor(breakdown.counts);
  const sharedRowSx = rowSx(statusColumns.length);
  return (
    <Box>
      <SectionHeader
        title="Discrepancies"
        action={
          // Staff keep Review all on an empty card: rows marked as a
          // non-discrepancy are left out of the count but can still be undone
          // in the review dialog.
          breakdown.total > 0 || canReview ? (
            <HeaderAction
              canReview={canReview}
              customerView={customerView}
              onOpenDiscrepancies={onOpenDiscrepancies}
            />
          ) : undefined
        }
      />

      {breakdown.total === 0 && !alwaysShowAllRows ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <CheckCircle sx={{ fontSize: "1.125rem", color: "success.main", flexShrink: 0 }} />
          <Typography variant="body2" color="text.secondary">
            {emptyMessage}
          </Typography>
        </Stack>
      ) : (
        <Box>
          {/* Column headers */}
          <Box sx={{ ...sharedRowSx, py: 0.25 }}>
            <span />
            {statusColumns.map((col) => (
              <Typography
                key={col.key}
                variant="caption"
                align="right"
                sx={{ color: "text.secondary", fontWeight: 500 }}
              >
                {col.label}
              </Typography>
            ))}
            <Typography
              variant="caption"
              align="right"
              sx={{ color: "text.secondary", fontWeight: 500 }}
            >
              Total
            </Typography>
          </Box>

          {rowsToShow.map((row) => (
            // Read-only top-line row (staff review via the "Review all" action).
            <Box key={row.key} sx={sharedRowSx}>
              <Typography variant="body2" sx={{ minWidth: 0 }}>
                {row.label}
              </Typography>
              <CountCells counts={row.counts} columns={statusColumns} />
            </Box>
          ))}
        </Box>
      )}

      {epaCorrectionDueInDays != null && (
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ mt: 1.5, color: epaCorrectionDueInDays < 0 ? "error.main" : "text.secondary" }}
        >
          <InfoOutlined sx={{ fontSize: "1rem", flexShrink: 0 }} />
          <Typography variant="caption">
            {epaCorrectionDueMessage(epaCorrectionDueInDays)}
          </Typography>
        </Stack>
      )}
    </Box>
  );
}
